/**
 * Bundle Discount Logic for Natelia Naturals
 * Handles automatic bundle discounts when certain product combinations are added to cart
 */
class BundleDiscount {
  constructor() {
    this.bundles = [
      {
        id: 'governor-royal-garlic',
        name: 'The Governor Bundle',
        requiredProducts: ['governor', 'royal-drop', 'black-garlic'],
        discount: 15, // 15% discount
        minQuantity: 3
      },
      {
        id: 'olive-oil-trio',
        name: 'Olive Oil Trio',
        requiredProducts: ['governor', 'royal-drop'],
        discount: 10, // 10% discount  
        minQuantity: 2
      }
    ];
    
    this.init();
  }

  init() {
    // Listen for cart updates
    document.addEventListener('DOMContentLoaded', () => {
      this.checkBundleEligibility();
    });
    
    // Listen for add to cart events
    document.addEventListener('cart:updated', () => {
      this.checkBundleEligibility();
    });
  }

  async checkBundleEligibility() {
    try {
      const cart = await this.getCart();
      const eligibleBundles = this.findEligibleBundles(cart);
      
      if (eligibleBundles.length > 0) {
        this.displayBundleOffer(eligibleBundles[0]); // Show the first eligible bundle
      } else {
        this.hideBundleOffer();
      }
    } catch (error) {
      console.error('Bundle discount error:', error);
    }
  }

  async getCart() {
    const response = await fetch('/cart.js');
    return await response.json();
  }

  findEligibleBundles(cart) {
    const cartProductHandles = cart.items.map(item => 
      item.handle || item.product_handle
    );
    
    return this.bundles.filter(bundle => {
      const hasAllProducts = bundle.requiredProducts.every(handle =>
        cartProductHandles.includes(handle)
      );
      
      const totalQuantity = cart.items
        .filter(item => bundle.requiredProducts.includes(item.handle || item.product_handle))
        .reduce((sum, item) => sum + item.quantity, 0);
        
      return hasAllProducts && totalQuantity >= bundle.minQuantity;
    });
  }

  displayBundleOffer(bundle) {
    // Remove existing bundle offers
    const existingOffer = document.querySelector('.bundle-offer');
    if (existingOffer) {
      existingOffer.remove();
    }

    // Create bundle offer element
    const bundleOffer = document.createElement('div');
    bundleOffer.className = 'bundle-offer';
    bundleOffer.innerHTML = `
      <div class="bundle-offer__content">
        <span class="bundle-offer__badge">Bundle Offer!</span>
        <p class="bundle-offer__text">
          You're eligible for <strong>${bundle.discount}% off</strong> with the ${bundle.name}
        </p>
        <button class="bundle-offer__apply button button--secondary" data-bundle-id="${bundle.id}">
          Apply ${bundle.discount}% Discount
        </button>
        <button class="bundle-offer__close" aria-label="Close offer">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
      </div>
    `;

    // Add styles
    bundleOffer.style.cssText = `
      position: fixed;
      bottom: 20px;
      right: 20px;
      background: linear-gradient(135deg, #f6f4f2 0%, #e8e2db 100%);
      border: 2px solid #B2906F;
      border-radius: 8px;
      padding: 20px;
      max-width: 320px;
      box-shadow: 0 4px 20px rgba(0,0,0,0.15);
      z-index: 1000;
      animation: slideIn 0.3s ease-out;
    `;

    // Add event listeners
    bundleOffer.querySelector('.bundle-offer__apply').addEventListener('click', () => {
      this.applyBundleDiscount(bundle);
    });

    bundleOffer.querySelector('.bundle-offer__close').addEventListener('click', () => {
      bundleOffer.remove();
    });

    document.body.appendChild(bundleOffer);

    // Auto-hide after 10 seconds
    setTimeout(() => {
      if (bundleOffer.parentNode) {
        bundleOffer.remove();
      }
    }, 10000);
  }

  hideBundleOffer() {
    const existingOffer = document.querySelector('.bundle-offer');
    if (existingOffer) {
      existingOffer.remove();
    }
  }

  async applyBundleDiscount(bundle) {
    try {
      // Get current cart
      const cart = await this.getCart();
      
      // Calculate discount
      const bundleItems = cart.items.filter(item => 
        bundle.requiredProducts.includes(item.handle || item.product_handle)
      );
      
      const bundleTotal = bundleItems.reduce((sum, item) => sum + item.final_line_price, 0);
      const discountAmount = Math.round(bundleTotal * (bundle.discount / 100));
      
      // Create discount code (this would normally be done server-side)
      const discountCode = `BUNDLE${bundle.discount}`;
      
      // Apply discount via Shopify's discount API
      const response = await fetch('/discount/' + discountCode, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (response.ok) {
        // Show success message
        this.showSuccessMessage(bundle, discountAmount);
        // Update cart display
        window.location.reload();
      } else {
        // Fallback: show manual discount instructions
        this.showManualDiscountInstructions(bundle);
      }
    } catch (error) {
      console.error('Error applying bundle discount:', error);
      this.showManualDiscountInstructions(bundle);
    }
  }

  showSuccessMessage(bundle, discountAmount) {
    const message = document.createElement('div');
    message.className = 'bundle-success';
    message.innerHTML = `
      <p><strong>Bundle discount applied!</strong></p>
      <p>You saved ${Shopify.formatMoney(discountAmount)} with ${bundle.name}</p>
    `;
    
    message.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #4CAF50;
      color: white;
      padding: 15px 20px;
      border-radius: 4px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.2);
      z-index: 1001;
    `;
    
    document.body.appendChild(message);
    
    setTimeout(() => {
      message.remove();
    }, 5000);
  }

  showManualDiscountInstructions(bundle) {
    const instructions = document.createElement('div');
    instructions.className = 'bundle-instructions';
    instructions.innerHTML = `
      <div class="bundle-instructions__content">
        <h3>Bundle Discount Available!</h3>
        <p>Use code <strong>BUNDLE${bundle.discount}</strong> at checkout for ${bundle.discount}% off your ${bundle.name}</p>
        <button class="bundle-instructions__copy">Copy Code</button>
        <button class="bundle-instructions__close">×</button>
      </div>
    `;
    
    instructions.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: white;
      padding: 30px;
      border-radius: 8px;
      box-shadow: 0 10px 40px rgba(0,0,0,0.3);
      z-index: 1002;
      max-width: 400px;
      text-align: center;
    `;
    
    // Add overlay
    const overlay = document.createElement('div');
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0,0,0,0.5);
      z-index: 1001;
    `;
    
    instructions.querySelector('.bundle-instructions__copy').addEventListener('click', () => {
      navigator.clipboard.writeText(`BUNDLE${bundle.discount}`);
      // Show copied feedback
    });
    
    const closeInstructions = () => {
      instructions.remove();
      overlay.remove();
    };
    
    instructions.querySelector('.bundle-instructions__close').addEventListener('click', closeInstructions);
    overlay.addEventListener('click', closeInstructions);
    
    document.body.appendChild(overlay);
    document.body.appendChild(instructions);
  }
}

// Initialize bundle discount system
new BundleDiscount();

// Add CSS animations
const style = document.createElement('style');
style.textContent = `
  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }

  .bundle-offer__content {
    position: relative;
  }

  .bundle-offer__badge {
    background: #B2906F;
    color: white;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .bundle-offer__text {
    margin: 10px 0;
    font-size: 14px;
    line-height: 1.4;
  }

  .bundle-offer__apply {
    width: 100%;
    margin-bottom: 10px;
  }

  .bundle-offer__close {
    position: absolute;
    top: -5px;
    right: -5px;
    background: rgba(0,0,0,0.1);
    border: none;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: rgba(0,0,0,0.6);
  }

  .bundle-offer__close:hover {
    background: rgba(0,0,0,0.2);
  }
`;

document.head.appendChild(style);